import React, { useState, Fragment, useEffect } from 'react';
import { icons } from '../../../assets/icons';
import './index.scss';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { TunisiaCities } from '../../../data';
import { getUserDetails, updateUserByAdmin } from '../../../api/redux/slices/Admin';
import { useDispatch } from 'react-redux';
import DeleteConfirmationModal from '../addUser/ShopDeleteModal';
import { v4 as uuidv4 } from 'uuid';
import { ButtonComponent } from '../../../components/buttons';
import { showToast } from '../../../components/modal';
import { LoaderComponent } from '../../../helpers/components/loader';

// Helper function to safely get value
const safeValue = (val) => {
    return val === null || val === undefined ? '' : val;
};

// Helper function to safely trim
const safeTrim = (val) => {
    if (val === null || val === undefined) return '';
    return String(val).trim();
};

const UpdateUser = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const params = useParams();
	const token = localStorage.getItem('accesstoken');
	const [shopToDeleteIndex, setShopToDeleteIndex] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [loading, setLoading] = useState(false);
	const [isFocus, setIsFocus] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [countries, setCountries] = useState([]);

	const [infos, setInfos] = useState({});
	const [client, setClient] = useState();
	const [shopFields, setShopFields] = useState();

	useEffect(() => {
		// Fetch countries
		fetch('https://cdn.jsdelivr.net/npm/world-countries@4/dist/countries.json')
			.then((res) => res.json())
			.then((data) => {
				const sortedCountries = data
					.map((country) => ({
						name: country?.name?.common,
						code: country?.cca3,
						flag: country?.flag,
					}))
					.filter((country) => country.name && country.code)
					.sort((a, b) => a.name.localeCompare(b.name));
				setCountries(sortedCountries);
			})
			.catch((err) => console.error('Error fetching countries:', err));
	}, []);

	useEffect(() => {
		setIsLoading(true);
		dispatch(getUserDetails({ token: token, id: params.id }))
			.unwrap()
			.then((res) => {
				setInfos(res.data);
				setShopFields(res.data?.shop?.map((el, i) => ({ ...el, id: uuidv4() })));
				setClient(res.data?.role === 'customer' ? true : false);
			})
			.finally(() => setIsLoading(false));
	}, [dispatch, token, params.id]);

	const addField = () => {
		let newfield = {
			id: uuidv4(),
			name: '',
			address_number: '',
			street: '',
			ville: '',
			postal_code: '',
			country: '',
			region: '',
		};

		setShopFields([...shopFields, newfield]);
	};

	const handleChangeShopForm = (id, e) => {
		let data = [...shopFields];
		const index = data.findIndex((item) => item.id === id);

		if (index !== -1) {
			e.target.name === 'postal_code'
				? (data[index][e.target.name] = e.target.value.replace(/\D/g, ''))
				: (data[index][e.target.name] = e.target.value);

			// Reset region if country is not Tunisia
			if (e.target.name === 'country' && e.target.value !== 'TUN') {
				data[index].region = '';
			}

			setShopFields(data);
			setIsFocus(true);
		}
	};

	const handleDeleteAddedShop = (id) => {
		if (shopFields.length > 1) {
			setShopFields(shopFields.filter((field) => field.id !== id));
			setShowDeleteModal(false);
			setShopToDeleteIndex(null);
		}
	};

	const confirmDeleteShop = (id) => {
		setShopToDeleteIndex(id);
		setShowDeleteModal(true);
	};

	const handleUpdateCommercial = (e) => {
		e.preventDefault();
		const data = {
			company_name: safeTrim(infos.company_name),
			email: safeTrim(infos.email),
			first_name: safeTrim(infos.first_name),
			last_name: safeTrim(infos.last_name),
			phone: safeTrim(infos.phone),
			tax_registration_number: safeTrim(infos.tax_registration_number), // ✅ Required
		};
		if (
			!data.company_name ||
			!data.email ||
			!data.first_name ||
			!data.last_name ||
			!data.phone ||
			!data.tax_registration_number
		) {
			showToast('Veuillez remplir tous les champs obligatoires', 'warn');
			return;
		}
		setLoading(true);
		dispatch(updateUserByAdmin({ token: token, id: params.id, data: data }))
			.unwrap()
			.then(() => {
				showToast('Utilisateur mis à jour', 'success', 'top-center', 1000);
				navigate(`/admin/users/user_details/${params.page}/${params.id}`);
			})
			.catch((err) => {
				showToast(err?.message || err, 'error');
			})
			.finally(() => setLoading(false));
	};

	const handleUpdateClient = (e) => {
		e.preventDefault();

		let canUpdateUser = true;
		let newShopFields = shopFields?.map((field) => {
			const data = {
				name: safeTrim(field.name),
				address_number: field.address_number || '',
				street: safeTrim(field.street),
				ville: safeTrim(field.ville),
				postal_code: field.postal_code || '',
				country: field.country || '',
				region: field.region || '',
				_id: field._id,
			};
			if (!data.name || !data.street || !data.ville || !data.country) {
				showToast('Veuillez remplir tous les champs du magasin', 'warn');
				canUpdateUser = false;
			}
			// Region only required for Tunisia
			if (data.country === 'TUN' && !data.region) {
				showToast('Veuillez sélectionner un gouvernorat', 'warn');
				canUpdateUser = false;
			}
			return data;
		});

		if (!canUpdateUser) return;

		const dataClient = {
			company_name: safeTrim(infos.company_name),
			email: safeTrim(infos.email),
			first_name: safeTrim(infos.first_name),
			last_name: safeTrim(infos.last_name),
			phone: safeTrim(infos.phone),
			tax_registration_number: safeTrim(infos.tax_registration_number), // ✅ Required
			customs_code: safeTrim(infos.customs_code) || undefined, // ✅ Optional - keep as undefined if empty
			categories: infos.categories,
			regulation: infos.regulation,
			is_client: true,
			shop: newShopFields,
		};

		if (
			!dataClient.company_name ||
			!dataClient.email ||
			!dataClient.first_name ||
			!dataClient.last_name ||
			!dataClient.phone ||
			!dataClient.tax_registration_number // ✅ Only tax_registration_number is required
		) {
			showToast('Veuillez remplir tous les champs obligatoires', 'warn');
			return;
		}

		setLoading(true);
		dispatch(updateUserByAdmin({ token: token, id: params.id, data: dataClient }))
			.unwrap()
			.then(() => {
				showToast('Utilisateur mis à jour', 'success', 'top-center', 1000);
				navigate(`/admin/users/user_details/${params.page}/${params.id}`);
			})
			.catch((err) => {
				showToast(err?.message || err, 'error');
			})
			.finally(() => setLoading(false));
	};

	const handleRadioChange = (categoryOrRegulation) => {
		setInfos({
			...infos,
			[categoryOrRegulation.type]: categoryOrRegulation.value,
		});
		setIsFocus(true);
	};

	if (isLoading) {
		return <LoaderComponent />;
	}

	return (
		<div id='update-user-admin'>
			<form onSubmit={client ? handleUpdateClient : handleUpdateCommercial}>
				<div className='navbar' style={{ width: 'calc(100vw - 20rem)', marginBottom: '2rem' }}>
					<Link
						to={`/admin/users/${params.page}`}
						style={{ width: '500px', display: 'flex', alignItems: 'center' }}
					>
						<icons.BsArrowLeft style={{ color: '#5B5B5B', marginRight: '10px' }} />
						<h1>Liste des clients</h1>
					</Link>

					<div className='submit-btns'>
						<div className='add-btns'>
							<Link to={`/admin/users/${params.page}`}>
								<button className={isFocus ? 'button-nature' : 'button-nature-inactive'}>
									Annuler
								</button>
							</Link>

							<ButtonComponent
								name='Enregistrer'
								loading={loading}
								type='submit'
								className={isFocus ? 'button-primary' : 'button-primary-inactive'}
							/>
						</div>
					</div>
				</div>

				{client && (
					<Fragment>
						<div className='user-infos'>
							<h2>Type catégorie</h2>
							<div className='line'>
								<input type='radio' name='usercategory' checked={infos.categories === 'detaillant'} onChange={() => handleRadioChange({ type: 'categories', value: 'detaillant' })} />
								<label>Détaillant</label>
							</div>
							<div className='line'>
								<input type='radio' name='usercategory' checked={infos.categories === 'grossiste'} onChange={() => handleRadioChange({ type: 'categories', value: 'grossiste' })} />
								<label>Grossiste</label>
							</div>
							<div className='line'>
								<input type='radio' name='usercategory' checked={infos.categories === 'monoprix'} onChange={() => handleRadioChange({ type: 'categories', value: 'monoprix' })} />
								<label>Grande surface</label>
							</div>
						</div>

						<div className='user-infos'>
							<h2>Type de règlement</h2>
							<div className='line'>
								<input type='radio' name='userreglement' checked={infos.regulation === 'espece'} onChange={() => handleRadioChange({ type: 'regulation', value: 'espece' })} />
								<label>Espèce</label>
							</div>
							<div className='line'>
								<input type='radio' name='userreglement' checked={infos.regulation === 'traite'} onChange={() => handleRadioChange({ type: 'regulation', value: 'traite' })} />
								<label>Traité</label>
							</div>
							<div className='line'>
								<input type='radio' name='userreglement' checked={infos.regulation === 'cheque'} onChange={() => handleRadioChange({ type: 'regulation', value: 'cheque' })} />
								<label>Chèque</label>
							</div>
						</div>
					</Fragment>
				)}

				<div className='user-details'>
					<h2>Information générale</h2>
					<div className='form-line'>
						<div className='field'>
							<label>Nom <span className="required">*</span></label>
							<input type='text' value={safeValue(infos.first_name)} onChange={(e) => { setInfos({ ...infos, first_name: e.target.value }); setIsFocus(true); }} required />
						</div>
						<div className='field'>
							<label>Prénom <span className="required">*</span></label>
							<input type='text' value={safeValue(infos.last_name)} onChange={(e) => { setInfos({ ...infos, last_name: e.target.value }); setIsFocus(true); }} required />
						</div>
					</div>
					<div className='field'>
						<label>Email <span className="required">*</span></label>
						<input type='email' value={safeValue(infos.email)} onChange={(e) => { setInfos({ ...infos, email: e.target.value }); setIsFocus(true); }} required />
					</div>
					<div className='field'>
						<label>Téléphone <span className="required">*</span></label>
						<input type='text' value={safeValue(infos.phone)} onChange={(e) => { setInfos({ ...infos, phone: e.target.value.replace(/[^0-9+\s]/g, '') }); setIsFocus(true); }} required />
					</div>
					<div className='field'>
						<label>Société <span className="required">*</span></label>
						<input type='text' value={safeValue(infos.company_name)} onChange={(e) => { setInfos({ ...infos, company_name: e.target.value }); setIsFocus(true); }} required />
					</div>
					
					{/* ✅ Matricule fiscal - REQUIRED */}
					<div className='field'>
						<label>Matricule fiscal <span className="required">*</span></label>
						<input 
							type='text' 
							value={safeValue(infos.tax_registration_number)} 
							onChange={(e) => { setInfos({ ...infos, tax_registration_number: e.target.value }); setIsFocus(true); }} 
							required
						/>
					</div>
					
					{/* ✅ Référence code client - OPTIONAL */}
					{client && (
						<div className='field'>
							<label>Référence code client <span style={{color: '#999', fontSize: '0.8rem'}}>(optionnel)</span></label>
							<input 
								type='text' 
								value={safeValue(infos.customs_code)} 
								onChange={(e) => { setInfos({ ...infos, customs_code: e.target.value }); setIsFocus(true); }} 
							/>
						</div>
					)}
				</div>

				{client && (
					<Fragment>
						<div className='add-shop-title'>
							<h2>Liste des boutiques</h2>
							<icons.BsPlusSquareDotted style={{ fontSize: '1.3rem', cursor: 'pointer' }} title='ajouter une nouvelle boutique' onClick={addField} />
						</div>
						{shopFields && shopFields.map((inputValue, index) => {
							return (
								<div className='shop' key={inputValue.id}>
									<div className='del-shop'>
										<div className='box-bordered' title='supprimer cette boutique'>
											<icons.BsTrash className='del-shop-icon' onClick={() => { confirmDeleteShop(inputValue.id); }} />
										</div>
									</div>
									<label>Nom de boutique <span className="required">*</span></label>
									<input type='text' placeholder='Nom de boutique' name='name' required value={safeValue(inputValue.name)} onChange={(e) => handleChangeShopForm(inputValue.id, e)} />
									<div className='delivery-details'>
										<label>Adresse</label>
										<div className='info-1'>
											<input type='number' placeholder='Numéro' name='address_number' required value={safeValue(inputValue.address_number)} onChange={(e) => handleChangeShopForm(inputValue.id, e)} onWheel={(e) => e.currentTarget.blur()} />
											<input type='text' placeholder='Rue' name='street' required value={safeValue(inputValue.street)} onChange={(e) => handleChangeShopForm(inputValue.id, e)} />
										</div>
										<div className='info-1'>
											<input type='text' placeholder='Ville' name='ville' required value={safeValue(inputValue.ville)} onChange={(e) => handleChangeShopForm(inputValue.id, e)} />
											<input type='text' placeholder='Code postal' name='postal_code' required value={safeValue(inputValue.postal_code)} onChange={(e) => handleChangeShopForm(inputValue.id, e)} />
										</div>
										<div className='info-dropdown'>
											<select name='country' value={safeValue(inputValue.country)} onChange={(e) => handleChangeShopForm(inputValue.id, e)} required>
												<option value=''>Choisir un pays</option>
												{countries.map((country, idx) => (
													<option key={idx} value={country.code}>{country.flag} {country.name}</option>
												))}
											</select>
										</div>
										{inputValue.country === 'TUN' && (
											<div className='info-dropdown'>
												<select required name='region' value={safeValue(inputValue.region)} onChange={(e) => handleChangeShopForm(inputValue.id, e)}>
													<option value=''>Choisir un gouvernorat</option>
													{TunisiaCities.map((city, idx) => (
														<option key={idx} value={city.name}>{city.name}</option>
													))}
												</select>
											</div>
										)}
									</div>
								</div>
							);
						})}
					</Fragment>
				)}
			</form>

			{showDeleteModal && (
				<DeleteConfirmationModal
					onDelete={() => { handleDeleteAddedShop(shopToDeleteIndex); }}
					onCancel={() => setShowDeleteModal(false)}
				/>
			)}
		</div>
	);
};

export default UpdateUser;